import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { createStelle, updateStelle } from '../api';
import { Pin as AnimatedPin } from './animate-ui/icons/Pin';
import { EllipsisVertical } from './animate-ui/icons/EllipsisVertical';
import { istEntwurf } from '../utils/themeUtils';
import { initialen } from '../utils/initialen';
import { setzeDragBild } from '../utils/dragGhost';

const PinIcon = ({ size = 16, filled = false, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 4h6a1 1 0 0 1 1 1v2a6 6 0 0 1-2 4.472V14a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-2.528A6 6 0 0 1 8 7V5a1 1 0 0 1 1-1z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={filled ? color : 'none'}
      fillOpacity={filled ? 0.2 : 0}
    />
    <line x1="12" y1="14" x2="12" y2="20" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const PencilIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4418_9717)">
      <path d="M13.26 3.59924L5.04997 12.2892C4.73997 12.6192 4.43997 13.2692 4.37997 13.7192L4.00997 16.9592C3.87997 18.1292 4.71997 18.9292 5.87997 18.7292L9.09997 18.1792C9.54997 18.0992 10.18 17.7692 10.49 17.4292L18.7 8.73924C20.12 7.23924 20.76 5.52924 18.55 3.43924C16.35 1.36924 14.68 2.09924 13.26 3.59924Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.89 5.05078C12.32 7.81078 14.56 9.92078 17.34 10.2008" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 22H21" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_4418_9717">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const TrashIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4418_9808)">
      <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.85 9.14062L18.2 19.2106C18.09 20.7806 18 22.0006 15.21 22.0006H8.79002C6.00002 22.0006 5.91002 20.7806 5.80002 19.2106L5.15002 9.14062" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.33 16.5H13.66" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.5 12.5H14.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_4418_9808">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

function StellenListe({ stellen, aktivId, onAuswahl, onGeaendert, markierteIds, onMarkierenToggle, onNeueStelle, neueLaedt, hasLebenslauf = false, profile, onBasisBearbeiten, onLebenslaufHinzufuegen, collapsed = false }) {
  const [umbenenneId, setUmbenenneId] = useState(null);
  const [umbenenneWert, setUmbenenneWert] = useState('');
  const [laedt, setLaedt] = useState(false);
  const istLaedt = neueLaedt !== undefined ? neueLaedt : laedt;
  const [menuOffenId, setMenuOffenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [lebenslaufHinweisOffen, setLebenslaufHinweisOffen] = useState(false);
  const [hinweisZaehler, setHinweisZaehler] = useState(0);

  const oeffneHinweis = () => {
    const key = !profile ? 'kl_hint_count_no_profile' : 'kl_hint_count_no_lebenslauf';
    const count = parseInt(sessionStorage.getItem(key) || '0') + 1;
    sessionStorage.setItem(key, String(count));
    setHinweisZaehler(count);
    setLebenslaufHinweisOffen(true);
  };
  const menuRef = useRef(null);
  const btnRefs = useRef({});
  const [typingMap, setTypingMap] = useState({});
  const prevNamesRef = useRef({});
  const typingTimersRef = useRef({});

  useEffect(() => {
    stellen.forEach(s => {
      const prev = prevNamesRef.current[s.id];
      if (prev === 'Neue Stellenanzeige' && s.name && s.name !== 'Neue Stellenanzeige') {
        clearInterval(typingTimersRef.current[s.id]);
        let i = 0;
        const fullName = s.name;
        typingTimersRef.current[s.id] = setInterval(() => {
          i++;
          setTypingMap(m => ({ ...m, [s.id]: fullName.slice(0, i) }));
          if (i >= fullName.length) {
            clearInterval(typingTimersRef.current[s.id]);
            setTimeout(() => setTypingMap(m => { const n = { ...m }; delete n[s.id]; return n; }), 700);
          }
        }, 40);
      }
      prevNamesRef.current[s.id] = s.name;
    });
  }, [stellen]);

  useEffect(() => () => Object.values(typingTimersRef.current).forEach(clearInterval), []);

  useEffect(() => {
    const onKlickAussen = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        !Object.values(btnRefs.current).some(btn => btn && btn.contains(e.target))
      ) {
        setMenuOffenId(null);
      }
    };
    if (menuOffenId !== null) {
      document.addEventListener('mousedown', onKlickAussen);
    }
    return () => document.removeEventListener('mousedown', onKlickAussen);
  }, [menuOffenId]);

  const onNeu = onNeueStelle
    ? onNeueStelle
    : async () => {
        setLaedt(true);
        try {
          const res = await createStelle({ name: 'Neue Stellenanzeige' });
          onGeaendert(res.data, 'neu');
        } finally {
          setLaedt(false);
        }
      };

  const onMenuToggle = (e, id) => {
    e.stopPropagation();
    if (menuOffenId === id) {
      setMenuOffenId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 6, left: rect.left - 180 + rect.width });
    setMenuOffenId(id);
  };

  const onPinToggle = (e, stelle) => {
    e.stopPropagation();
    onMarkierenToggle(stelle.id);
    setMenuOffenId(null);
  };

  const onUmbenennenStart = (e, stelle) => {
    e.stopPropagation();
    setMenuOffenId(null);
    setUmbenenneId(stelle.id);
    setUmbenenneWert(stelle.name);
  };

  const onUmbenennenSpeichern = async (stelle) => {
    if (!umbenenneWert.trim()) return;
    try {
      await updateStelle(stelle.id, { name: umbenenneWert });
      onGeaendert({ ...stelle, name: umbenenneWert }, 'update');
    } finally {
      setUmbenenneId(null);
    }
  };

  const onLoeschen = async (e, stelle) => {
    e.stopPropagation();
    setMenuOffenId(null);
    const neuerStatus = `papierkorb#${stelle.bewerbungs_status || ''}#${new Date().toISOString()}`;
    await updateStelle(stelle.id, { bewerbungs_status: neuerStatus });
    onGeaendert({ ...stelle, bewerbungs_status: neuerStatus }, 'papierkorb');
  };

  const sichtbare = stellen.filter(s => s.id === aktivId || s.text || s.url);
  const sortiert = [...sichtbare].sort((a, b) => {
    const tDiff = new Date(b.erstellt_am || 0) - new Date(a.erstellt_am || 0);
    if (tDiff !== 0) return tDiff;
    return a.id < b.id ? 1 : -1;
  });

  const markierte = sortiert.filter(s => markierteIds.has(s.id));
  const nichtMarkierte = sortiert.filter(s => !markierteIds.has(s.id));

  const renderItem = (s) => {
    const isPinned = markierteIds.has(s.id);
    const isEntwurf = istEntwurf(s);
    return (
      <div
        key={s.id}
        className={`stellen-item${s.id === aktivId ? ' aktiv' : ''}${isPinned ? ' gepinnt' : ''}`}
        onClick={() => onAuswahl(s)}
        draggable
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', s.id); e.dataTransfer.effectAllowed = 'move'; setzeDragBild(e); }}
      >
        {umbenenneId === s.id ? (
          <input
            className="stellen-umbenennen"
            value={umbenenneWert}
            autoFocus
            onChange={(e) => setUmbenenneWert(e.target.value)}
            onBlur={() => onUmbenennenSpeichern(s)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onUmbenennenSpeichern(s);
              if (e.key === 'Escape') setUmbenenneId(null);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            {collapsed && (
              <span className="stellen-monogram" title={s.name}>{initialen(s.name)}</span>
            )}
            {(isEntwurf || s._runningStatus || (!s.generiert_am && (s.text || s.url) && !s.bewerbungs_status)) && <span className="stellen-entwurf-dot" title="Noch nicht generiert" />}
            <span className="stellen-name">
              {typingMap[s.id] !== undefined
                ? <>{typingMap[s.id]}<span className="stellen-typing-cursor">|</span></>
                : s.name}
            </span>
            <div className={`stellen-aktionen${menuOffenId === s.id ? ' menu-offen' : ''}`}>
              <button
                className={`stellen-pin-btn${isPinned ? ' ist-gepinnt' : ''}`}
                title={isPinned ? 'Pin entfernen' : 'Anpinnen'}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => onPinToggle(e, s)}
              >
                <AnimatedPin
                  size={22}
                  animateOnHover
                  style={{ display: 'block' }}
                />
              </button>
              <button
                className="stellen-dots-btn"
                title="Optionen"
                ref={(el) => { btnRefs.current[s.id] = el; }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => onMenuToggle(e, s.id)}
              >
                <EllipsisVertical size={23} animateOnHover style={{ display: 'block' }} />
              </button>
              {menuOffenId === s.id && ReactDOM.createPortal(
                <div
                  className="stellen-dropdown"
                  ref={menuRef}
                  style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button className="stellen-dropdown-item" onClick={(e) => onUmbenennenStart(e, s)}>
                    <PencilIcon size={15} />
                    <span>Umbenennen</span>
                  </button>
                  <div className="stellen-dropdown-divider" />
                  <button className="stellen-dropdown-item danger" onClick={(e) => onLoeschen(e, s)}>
                    <TrashIcon size={15} />
                    <span>Löschen</span>
                  </button>
                </div>,
                document.body
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={`stellen-liste${collapsed ? ' is-collapsed' : ''}`}>
      <div className="stellen-liste-header">
        <span>Stellenanzeigen</span>
      </div>

      <div className="stellen-liste-scroll-wrap">
        <div className="stellen-liste-inhalt">
          {markierte.length > 0 && (
            <>
              <div className="stellen-sektion-label stellen-sektion-pinned" />
              {markierte.map(renderItem)}
              {nichtMarkierte.length > 0 && (
                <div className="stellen-sektion-label stellen-sektion-alle" />
              )}
            </>
          )}
          {nichtMarkierte.map(renderItem)}
        </div>
        <div className="stellen-liste-blur" aria-hidden="true" />
      </div>

      {!hasLebenslauf && lebenslaufHinweisOffen && ReactDOM.createPortal(
        <div className="kein-lebenslauf-overlay" onClick={() => setLebenslaufHinweisOffen(false)}>
          <div className="kein-lebenslauf-insel" onClick={e => e.stopPropagation()}>
            <button className="kein-lebenslauf-insel__close" onClick={() => setLebenslaufHinweisOffen(false)}>✕</button>
            {hinweisZaehler >= 2 ? (
              <>
                <span className="kein-lebenslauf-insel__icon">😄</span>
                <h3>Hey, fehlt immer noch! 😄</h3>
                <p>Wir sehen uns jetzt schon zum {hinweisZaehler}. Mal… Kurz {!profile ? 'die Stammdaten & den Lebenslauf' : 'den Lebenslauf'} ergänzen – dann klappt's endlich! 😅</p>
                <button className="kein-lebenslauf-insel__cta" onClick={() => { setLebenslaufHinweisOffen(false); profile ? onLebenslaufHinzufuegen?.() : onBasisBearbeiten?.(); }}>
                  {!profile ? 'Stammdaten ergänzen →' : 'Lebenslauf hinzufügen →'}
                </button>
              </>
            ) : !profile ? (
              <>
                <span className="kein-lebenslauf-insel__icon">✋</span>
                <h3>Stammdaten & Lebenslauf fehlen noch</h3>
                <p>Trage zuerst deine Stammdaten ein und füge danach deinen Lebenslauf hinzu – dann kann's losgehen!</p>
                <button className="kein-lebenslauf-insel__cta" onClick={() => { setLebenslaufHinweisOffen(false); onBasisBearbeiten?.(); }}>
                  Stammdaten ergänzen →
                </button>
              </>
            ) : (
              <>
                <span className="kein-lebenslauf-insel__icon">👋</span>
                <h3>Lebenslauf ergänzen</h3>
                <p>Du bist mit Profil „{profile.vorname} {profile.nachname}" angemeldet. Lade deinen Lebenslauf hoch oder erstelle eigene Sektionen – dann kann's losgehen!</p>
                <button className="kein-lebenslauf-insel__cta" onClick={() => { setLebenslaufHinweisOffen(false); onLebenslaufHinzufuegen?.(); }}>
                  Lebenslauf hinzufügen →
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
      <button
        className={`stellen-neu-btn${!hasLebenslauf ? ' stellen-neu-btn--gesperrt' : ''}`}
        onClick={hasLebenslauf ? onNeu : oeffneHinweis}
        disabled={istLaedt}
        data-tour="job-input"
      >
        {istLaedt ? '...' : (<><span className="stellen-neu-plus">+</span><span className="stellen-neu-label"> Neue Stelle</span></>)}
      </button>
    </div>
  );
}

export default StellenListe;
